import { useCallback, useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { editorSelectors } from 'store/features/editor/selectors'
import debounce from 'lodash.debounce'
import { dispatch } from 'store'
import { IMediaItem } from 'types/media'

export const FULL_VOLUME = 100

// All selected items share the same volume → show it; mixed (or unset) → 100%.
const getCommonVolume = (volumes: (number | undefined)[]) => {
  const [first] = volumes
  const allSame = volumes.every(volume => volume === first)
  return allSame && typeof first === 'number' ? first : FULL_VOLUME
}

export const useSelectedAudioVolumeUpdatesHandler = () => {
  const mediaItems = useSelector(projectDataSelectors.mediaItems)
  const multilineAudios = useSelector(projectDataSelectors.multilineAudios)
  const activeMediaSelection = useSelector(editorSelectors.activeMediaItems)
  const activeAudioSelection = useSelector(editorSelectors.activeAudioItems)

  const activeMediaItems = useMemo(
    () =>
      mediaItems.filter(item =>
        activeMediaSelection.some(active => active.id === item.id)
      ),
    [mediaItems, activeMediaSelection]
  )

  const activeAudioItems = useMemo(
    () =>
      multilineAudios.filter(item =>
        activeAudioSelection.some(active => active.id === item.id)
      ),
    [multilineAudios, activeAudioSelection]
  )

  const initialVolume = useMemo(
    () =>
      getCommonVolume([
        ...activeMediaItems.map(item => item.meta.volume),
        ...activeAudioItems.map(item => item.volume)
      ]),
    [activeMediaItems, activeAudioItems]
  )

  const [volume, setVolume] = useState(initialVolume)

  // The audio thunk reads the active audio items from state itself; media items
  // each carry their own `meta`, so they are passed through and updated per item.
  const debounceChange = useMemo(
    () =>
      debounce((value: number, media: IMediaItem[]) => {
        media.forEach(item =>
          dispatch(
            projectDataThunks.updateMediaItem({
              id: item.id,
              updates: { meta: { ...item.meta, volume: value } }
            })
          )
        )
        dispatch(projectDataThunks.updateActiveAudioVolume(value))
      }, 400),
    []
  )

  const onChangeVolume = useCallback(
    (newValue: number) => {
      setVolume(newValue)
      debounceChange(newValue, activeMediaItems)
    },
    [debounceChange, activeMediaItems]
  )

  useEffect(() => {
    setVolume(initialVolume)
  }, [initialVolume])

  return {
    onChangeVolume,
    volume
  }
}
